// src/pages/Realisations.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { fadeIn, scaleIn } from '../styles/animations';
import { realizationsService, categoriesService } from '../services/api';
import SectionTitle from '../components/ui/SectionTitle';
import Button from '../components/ui/Button';

const RealisationsPageWrapper = styled.div`
  padding-top: 80px; // Pour compenser la hauteur du header fixe
`;

const HeroSection = styled.section`
  background-color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.xl} 0;
  color: ${props => props.theme.colors.light};
  text-align: center;
`;

const HeroContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const HeroTitle = styled.h1`
  color: ${props => props.theme.colors.light};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.xxxl};
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.xxl};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: 0;
  opacity: 0.9;
  color: ${props => props.theme.colors.light};
`;

const RealisationsContent = styled.section`
  padding: ${props => props.theme.spacing.xl} 0;
  background-color: ${props => props.theme.colors.gray};
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.md};
  border-bottom: 1px solid #eee;
  animation: ${fadeIn} 0.8s ease-out forwards;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const FilterTitle = styled.h3`
  margin: 0;
  margin-right: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.medium};
  display: flex;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    margin-bottom: ${props => props.theme.spacing.xs};
  }
`;

const FilterButton = styled.button`
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.light};
  color: ${props => props.active ? props.theme.colors.light : props.theme.colors.textMain};
  padding: 0.5rem 1rem;
  border-radius: ${props => props.theme.borderRadius.small};
  border: 1px solid ${props => props.active ? props.theme.colors.primary : '#ddd'};
  font-size: ${props => props.theme.fontSizes.small};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  
  &:hover {
    background-color: ${props => props.active ? props.theme.colors.primaryDark : '#f5f5f5'};
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  background-color: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.small};
  transition: all ${props => props.theme.transitions.medium};
  animation: ${scaleIn} 0.6s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.medium};
    
    .project-image img {
      transform: scale(1.05);
    }
  }
`;

const ProjectImage = styled.div`
  height: 240px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform ${props => props.theme.transitions.medium};
  }
`;

const ProjectContent = styled.div`
  padding: ${props => props.theme.spacing.md};
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const ProjectTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: 0;
`;

const ProjectLocation = styled.span`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const ProjectDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} 0;
  color: ${props => props.theme.colors.danger};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} 0;
  
  p {
    margin-bottom: ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.textLight};
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${props => props.theme.spacing.xl};
  gap: ${props => props.theme.spacing.xs};
`;

const PageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.small};
  border: 1px solid ${props => props.active ? props.theme.colors.primary : '#ddd'};
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.light};
  color: ${props => props.active ? props.theme.colors.light : props.theme.colors.textMain};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  
  &:hover {
    background-color: ${props => props.active ? props.theme.colors.primaryDark : '#f5f5f5'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.light};
    }
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} 0;
  
  h3 {
    margin-bottom: ${props => props.theme.spacing.md};
  }
  
  p {
    margin-bottom: ${props => props.theme.spacing.lg};
    color: ${props => props.theme.colors.textLight};
  }
`;

const Realisations = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [projects, setProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        current_page: 1,
        total_pages: 1,
        total_items: 0,
        per_page: 6
    });

    // Nombre de projets par page
    const projectsPerPage = 6;

    // Charger les catégories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setError(null);
                const categoriesData = await categoriesService.getAll();
                // Ajouter une catégorie "Toutes les réalisations" en premier
                setCategories([
                    { id: 'all', name: 'Toutes les réalisations' },
                    ...categoriesData
                ]);
            } catch (err) {
                console.error('Erreur lors du chargement des catégories:', err);
                setError('Erreur lors du chargement des catégories');
            }
        };

        fetchCategories();
    }, []);

    // Charger les réalisations
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                setLoading(true);
                setError(null);

                // Paramètres pour l'API
                const params = {
                    page: 1,
                    limit: 100 // Récupérer un grand nombre pour permettre la pagination côté client
                };

                // Si une catégorie spécifique est sélectionnée
                if (selectedCategory !== 'all' && selectedCategory !== '') {
                    const category = categories.find(c => c.id.toString() === selectedCategory);
                    if (category) {
                        params.category_id = category.id;
                    }
                }

                let response;
                if (selectedCategory === 'all' || selectedCategory === '') {
                    response = await realizationsService.getAll(params);
                } else {
                    // Trouver le slug de la catégorie à partir de son ID
                    const category = categories.find(c => c.id.toString() === selectedCategory);
                    if (category && category.slug) {
                        response = await realizationsService.getByCategory(category.slug);
                    } else {
                        response = await realizationsService.getAll(params);
                    }
                }

                // Mise à jour de l'état avec les données reçues
                if (response) {
                    if (response.realizations) {
                        setProjects(response.realizations);
                        if (response.pagination) {
                            setPagination(response.pagination);
                        }
                    } else if (Array.isArray(response)) {
                        setProjects(response);
                    }
                }

                setLoading(false);
            } catch (err) {
                console.error('Erreur lors du chargement des réalisations:', err);
                setError('Erreur lors du chargement des réalisations');
                setLoading(false);
            }
        };

        // Seulement charger les projets si les catégories sont chargées
        if (categories.length > 0) {
            fetchProjects();
        }
    }, [selectedCategory, categories]);

    // Filtre les projets en fonction de la catégorie sélectionnée
    useEffect(() => {
        // Appliquer les filtres côté client
        const filtered = selectedCategory === 'all'
            ? projects
            : projects.filter(project => project.category_id?.toString() === selectedCategory);

        setFilteredProjects(filtered);
        setCurrentPage(1); // Réinitialise la page à 1 lors du changement de filtre

        // Mettre à jour la pagination
        setPagination(prev => ({
            ...prev,
            current_page: 1,
            total_pages: Math.ceil(filtered.length / projectsPerPage),
            total_items: filtered.length
        }));
    }, [selectedCategory, projects, projectsPerPage]);

    // Fonction pour la pagination
    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        // Faire défiler vers le haut de la page
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Obtenir les projets de la page courante
    const getCurrentProjects = () => {
        const indexOfLastProject = currentPage * projectsPerPage;
        const indexOfFirstProject = indexOfLastProject - projectsPerPage;
        return filteredProjects.slice(indexOfFirstProject, indexOfLastProject);
    };

    // Changer la catégorie sélectionnée
    const handleCategoryChange = (categoryId) => {
        setSelectedCategory(categoryId);
        setCurrentPage(1); // Réinitialiser à la page 1
    };

    return (
        <RealisationsPageWrapper>
            <Helmet>
                <title>Nos Réalisations - Decko | Créateur de vos parquets de jardin</title>
                <meta name="description" content="Découvrez nos réalisations de terrasses en bois, parquets extérieurs et aménagements sur mesure pour espaces de vie extérieurs." />
            </Helmet>

            <HeroSection>
                <HeroContainer>
                    <HeroTitle>Nos Réalisations</HeroTitle>
                    <HeroSubtitle>Découvrez nos créations personnalisées</HeroSubtitle>
                </HeroContainer>
            </HeroSection>

            <RealisationsContent>
                <ContentContainer>
                    {error && <ErrorMessage>{error}</ErrorMessage>}

                    <FilterBar>
                        <FilterTitle>Filtrer par :</FilterTitle>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            {categories.map(category => (
                                <FilterButton
                                    key={category.id}
                                    active={selectedCategory === category.id.toString()}
                                    onClick={() => handleCategoryChange(category.id.toString())}
                                >
                                    {category.name}
                                </FilterButton>
                            ))}
                        </div>
                    </FilterBar>

                    {loading ? (
                        <LoadingIndicator>
                            <p>Chargement des réalisations...</p>
                        </LoadingIndicator>
                    ) : filteredProjects.length === 0 ? (
                        <NoResults>
                            <h3>Aucune réalisation trouvée</h3>
                            <p>Aucune réalisation ne correspond à la catégorie sélectionnée.</p>
                            <Button
                                variant="secondary"
                                onClick={() => handleCategoryChange('all')}
                            >
                                Voir toutes les réalisations
                            </Button>
                        </NoResults>
                    ) : (
                        <>
                            <ProjectsGrid>
                                {getCurrentProjects().map((project, index) => (
                                    <ProjectCard key={project.id} delay={`${0.1 * index}s`}>
                                        <ProjectImage className="project-image">
                                            <img
                                                src={project.featured_image
                                                    ? `https://decko.fr/uploads/${project.featured_image}`
                                                    : 'https://via.placeholder.com/400x240?text=Decko+Réalisations'}
                                                alt={project.title}
                                            />
                                        </ProjectImage>
                                        <ProjectContent>
                                            <ProjectHeader>
                                                <ProjectTitle>{project.title}</ProjectTitle>
                                                <ProjectLocation>{project.location || ''}</ProjectLocation>
                                            </ProjectHeader>
                                            <ProjectDescription>{project.description}</ProjectDescription>
                                            <Button
                                                variant="text"
                                                to={`/realisations/${project.id}`}
                                            >
                                                Voir le projet
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                    <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z" />
                                                </svg>
                                            </Button>
                                        </ProjectContent>
                                    </ProjectCard>
                                ))}
                            </ProjectsGrid>

                            {pagination.total_pages > 1 && (
                                <Pagination>
                                    <PageButton
                                        onClick={() => paginate(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        &lt;
                                    </PageButton>

                                    {[...Array(pagination.total_pages)].map((_, i) => (
                                        <PageButton
                                            key={i + 1}
                                            active={currentPage === i + 1}
                                            onClick={() => paginate(i + 1)}
                                        >
                                            {i + 1}
                                        </PageButton>
                                    ))}

                                    <PageButton
                                        onClick={() => paginate(currentPage + 1)}
                                        disabled={currentPage === pagination.total_pages}
                                    >
                                        &gt;
                                    </PageButton>
                                </Pagination>
                            )}
                        </>
                    )}
                </ContentContainer>
            </RealisationsContent>
        </RealisationsPageWrapper>
    );
};

export default Realisations;